import * as aiScannerSession from '../utils/aiScannerSession';
import { redirectToLoginIfRequired } from '../utils/common';
import Logger from '../utils/logger';
import { fillMissingResultMetadata } from '../utils/scanCandidates';
import { getMarketSdk, getUserSdk } from '../utils/sdk';
import * as time from '../utils/time';
import types from './_actionTypes';

function validateUser(user) {
  if (!user || !user.id || user.tokenIsValid !== true || !user.token) {
    Logger.error({
      errorCode: 'E0022',
      message: 'Invalid Token'
    });
    return false;
  }
  return true;
}

function initializeMarketSdk(user) {
  if (!validateUser(user)) {
    return null;
  }
  return getMarketSdk(user.token);
}

function initializeUserSdk(user) {
  if (!validateUser(user)) {
    return null;
  }
  return getUserSdk(user.token);
}

function receiveScanResponse(scanName, response) {
  return {
    type: types.RECEIVE_SCAN,
    name: scanName,
    data: response
  };
}

function receiveUserScansResponse(response) {
  return {
    type: types.RECEIVE_USER_SCANS,
    data: response
  };
}

function receiveLastScanResponse(response) {
  return {
    type: types.RECEIVE_LAST_SCAN,
    data: response
  };
}

function saveLastScanResponse(scan) {
  return {
    type: types.SAVE_LAST_SCAN,
    data: scan
  };
}

function saveScanResponse(scan) {
  return {
    type: types.SAVE_SCAN,
    data: scan
  };
}

function overwriteScanResponse(scan) {
  return {
    type: types.OVERWRITE_SCAN,
    data: scan
  };
}

function deleteScanResponse(response, scanId) {
  return {
    type: types.DELETE_SCAN,
    id: scanId,
    data: response
  };
}

export function errorRequestingScan(scanName, error) {
  return {
    type: types.ERROR_REQUESTING_SCAN,
    name: scanName,
    error: error,
    errorCode: 'E0018'
  };
}

export function errorRequestingUserScans(error) {
  return {
    type: types.ERROR_REQUESTING_USER_SCANS,
    error: error,
    errorCode: 'E0032'
  };
}

export function errorRequestingLastScan(error) {
  return {
    type: types.ERROR_REQUESTING_LAST_SCAN,
    error: error,
    errorCode: 'E0033'
  };
}

export function errorSavingLastScan(lastScan, error) {
  return {
    type: types.ERROR_SAVING_LAST_SCAN,
    lastScan: lastScan,
    error: error,
    errorCode: 'E0034'
  };
}

export function errorSavingScan(scan, error) {
  return {
    type: types.ERROR_SAVING_SCAN,
    scan: scan,
    error: error,
    errorCode: 'E0035'
  };
}

export function errorOverwritingScan(scan, error) {
  return {
    type: types.ERROR_OVERWRITING_SCAN,
    scan: scan,
    error: error,
    errorCode: 'E0036'
  };
}

export function errorDeletingScan(scanId, error) {
  return {
    type: types.ERROR_DELETING_SCAN,
    scanId: scanId,
    error: error,
    errorCode: 'E0037'
  };
}

export function errorRenamingScan(scan, error) {
  return {
    type: types.ERROR_RENAMING_SCAN,
    scan: scan,
    error: error,
    errorCode: 'E0042'
  };
}

export function setSelectedScan(scan) {
  return {
    type: types.SET_SELECTED_SCAN,
    data: scan
  };
}

export function setScanCriteriaEnabled(fieldId, enabled) {
  return {
    type: types.SET_ENABLED_SCAN_CRITERIA,
    fieldId,
    enabled
  };
}

export function resetScanCriteriaEnabled() {
  return {
    type: types.RESET_ENABLED_SCAN_CRITERIA
  };
}

/**
 * STORY-009: records one AI Scanner investment answer.
 *
 * @param {'q1'|'q2'} questionId - Which question is being answered.
 * @param {'A'|'B'|'C'} code - The selected option's captured code.
 */
export function setAiScannerAnswer(questionId, code) {
  return {
    type: types.SET_AI_SCANNER_ANSWER,
    questionId,
    code
  };
}

/** STORY-009: clears both AI Scanner answers (overlay close/reopen). */
export function resetAiScannerAnswers() {
  return {
    type: types.RESET_AI_SCANNER_ANSWERS
  };
}

/**
 * How long a cached scan is considered fresh, in seconds.
 *
 * Set to 60s per team discussion.
 */
export const SCAN_STALE_SECONDS = 60;

/**
 * Whether a scan needs to be (re)fetched from Syphon.
 *
 * Mirrors `shouldFetchSymbol` in `src/actions/symbol.js`: never fetched or a
 * previous attempt errored means fetch; otherwise fetch only once the cached
 * result is older than {@link SCAN_STALE_SECONDS}.
 *
 * NOTE: in-flight requests are NOT considered here — the AI Scanner tracks
 * those at the call site. Callers that can issue concurrent requests for the
 * same scan must de-duplicate themselves.
 *
 * @param {object} state - the Redux state
 * @param {string} scanName
 * @returns {boolean}
 */
export function shouldFetchScan(state, scanName) {
  if (!scanName) {
    return false;
  }

  const key = String(scanName).toLowerCase();
  const scans = (state && state.scans) || {};
  const cached = scans.scanData && scans.scanData[key];
  const errored = scans.scanErrors && scans.scanErrors[key];

  if (!cached || !cached.symbols) {
    return true;
  }
  if (errored) {
    return true;
  }
  // Data stored before `when` was introduced (or by a test fixture) has no
  // stamp; treat it as stale rather than pinning it in the cache forever.
  if (!cached.when) {
    return true;
  }
  return cached.when < time.timestamp() - SCAN_STALE_SECONDS;
}

/**
 * Fetches a scan only when the cached copy is missing, errored or stale.
 *
 * The read-through wrapper around {@link fetchScan}, following the
 * `fetchSymbolIfNeeded` pattern. Callers get a promise either way, so the
 * fetch/no-fetch paths can be awaited uniformly; it resolves to `false` when
 * nothing was requested.
 *
 * @param {string} scanName
 * @returns {Function} Redux thunk function
 * @example
 * dispatch(fetchScanIfNeeded('MarketCap'))
 */
export function fetchScanIfNeeded(scanName) {
  return (dispatch, getState) => {
    if (!shouldFetchScan(getState(), scanName)) {
      return Promise.resolve(false);
    }
    return dispatch(fetchScan(scanName));
  };
}

/**
 * Fetches scan results using the insight-market-sdk. Always issues a request —
 * use {@link fetchScanIfNeeded} when a fresh cached copy should be reused.
 *
 * @param {string} scanName - The name of the scan to fetch
 * @returns {Function} Redux thunk function
 * @example
 * dispatch(fetchScan('SecurityType'))
 */
export function fetchScan(scanName) {
  return (dispatch, getState) => {
    const sdk = initializeMarketSdk(getState().user);
    if (!sdk) {
      dispatch(
        errorRequestingScan(
          scanName,
          new Error('SDK initialization failed - invalid or missing token')
        )
      );
      return false;
    }

    return sdk.scans
      .getScanResults(scanName)
      .then((response) => {
        // Validate SDK response structure
        if (!response) {
          throw new Error(`SDK returned null/undefined for scan: ${scanName}`);
        }

        // The SDK response is directly an object of symbol names/IDs
        // Structure: { [symbolId]: { filterValue: string, displayValues: {...} } }
        if (typeof response !== 'object' || Array.isArray(response)) {
          throw new Error(
            `SDK response must be an object with symbol IDs as keys. Received: ${JSON.stringify(
              response
            )}`
          );
        }

        // Transform to expected format: { data: { symbols: response }, ok: true }
        // The response itself is the symbols object
        const transformedResponse = {
          data: {
            symbols: response
          },
          ok: true
        };

        return dispatch(receiveScanResponse(scanName, transformedResponse));
      })
      .catch((ex) => {
        redirectToLoginIfRequired(ex);
        dispatch(errorRequestingScan(scanName, ex));
      });
  };
}

/**
 * Fetches user scans using the insight-user-sdk
 * @returns {Function} Redux thunk function
 * @example
 * dispatch(fetchUserScans())
 */
export function fetchUserScans() {
  return (dispatch, getState) => {
    const sdk = initializeUserSdk(getState().user);
    if (!sdk) {
      dispatch(
        errorRequestingUserScans(
          new Error('SDK initialization failed - invalid or missing token')
        )
      );
      return false;
    }

    return sdk.scans
      .getAll()
      .then((response) => {
        // Validate SDK response
        if (!response) {
          throw new Error('SDK returned null/undefined for user scans');
        }

        // Transform SDK response to match expected format
        // SDK returns: { scans: Scan[], staticScans: Scan[] }
        // Expected: { data: { scans: Scan[], staticScans: Scan[] } }
        const transformedResponse = {
          data: {
            scans: response.scans || [],
            staticScans: response.staticScans || []
          }
        };

        return dispatch(receiveUserScansResponse(transformedResponse));
      })
      .catch((ex) => {
        redirectToLoginIfRequired(ex);
        dispatch(errorRequestingUserScans(ex));
      });
  };
}

/**
 * Fetches the last scan using the insight-user-sdk
 * @returns {Function} Redux thunk function
 * @example
 * dispatch(fetchLastScan())
 */
export function fetchLastScan() {
  return (dispatch, getState) => {
    const sdk = initializeUserSdk(getState().user);
    if (!sdk) {
      dispatch(
        errorRequestingLastScan(
          new Error('SDK initialization failed - invalid or missing token')
        )
      );
      return false;
    }

    return sdk.scans
      .getById('last-scan')
      .then((scan) => {
        // Validate SDK response
        if (!scan) {
          throw new Error('SDK returned null/undefined for last scan');
        }

        // Transform SDK response to match expected format
        // SDK returns: Scan object
        // Expected: { data: { scan: Scan } }
        const transformedResponse = {
          data: {
            scan: scan
          }
        };

        return dispatch(receiveLastScanResponse(transformedResponse));
      })
      .catch((ex) => {
        redirectToLoginIfRequired(ex);
        dispatch(errorRequestingLastScan(ex));
      });
  };
}

/**
 * Saves the last scan using the insight-user-sdk
 * @param {Object} lastScan - The last scan object to save
 * @returns {Function} Redux thunk function
 * @example
 * dispatch(saveLastScan(scanObject))
 */
export function saveLastScan(lastScan) {
  return (dispatch, getState) => {
    const sdk = initializeUserSdk(getState().user);
    if (!sdk) {
      const error = new Error(
        'SDK initialization failed - invalid or missing token'
      );
      dispatch(errorSavingLastScan(lastScan, error));
      return false;
    }

    return sdk.scans
      .replaceById('last-scan', lastScan)
      .then(() => {
        // SDK returns void on success
        // Transform to expected format for reducer
        const transformedResponse = lastScan;

        return dispatch(saveLastScanResponse(transformedResponse));
      })
      .catch((ex) => {
        redirectToLoginIfRequired(ex);
        dispatch(errorSavingLastScan(lastScan, ex));
      });
  };
}

/**
 * Creates a new user scan using the insight-user-sdk
 * @param {Object} newScan - The scan object to create
 * @param {Function} handleSuccess - Success callback receiving the scan ID
 * @param {Function} handleFail - Failure callback
 * @returns {Function} Redux thunk function
 * @example
 * dispatch(saveScan(scanObject, successCallback, failCallback))
 */
export function saveScan(newScan, handleSuccess, handleFail) {
  return (dispatch, getState) => {
    const sdk = initializeUserSdk(getState().user);
    if (!sdk) {
      const error = new Error(
        'SDK initialization failed - invalid or missing token'
      );
      dispatch(errorSavingScan(newScan, error));
      if (handleFail) handleFail(error);
      return false;
    }

    return sdk.scans
      .add(newScan)
      .then((createdScan) => {
        // Validate SDK response
        if (!createdScan || !createdScan.id) {
          throw new Error('SDK returned invalid scan response');
        }

        // Transform to expected format: { data: { scan: createdScan } }
        const transformedResponse = {
          data: {
            scan: createdScan
          }
        };

        // Call success handler with the generated scan ID
        if (handleSuccess) {
          handleSuccess(createdScan.id);
        }

        return dispatch(saveScanResponse(transformedResponse));
      })
      .catch((ex) => {
        redirectToLoginIfRequired(ex);
        dispatch(errorSavingScan(newScan, ex));
        if (handleFail) handleFail(ex);
      });
  };
}

/**
 * Updates an existing user scan using the insight-user-sdk
 * @param {Object} existingScan - The scan object to update (must include id)
 * @param {Function} handleSuccess - Success callback
 * @param {Function} handleFail - Failure callback
 * @returns {Function} Redux thunk function
 * @example
 * dispatch(overwriteScan(scanObject, successCallback, failCallback))
 */
export function overwriteScan(existingScan, handleSuccess, handleFail) {
  return (dispatch, getState) => {
    const sdk = initializeUserSdk(getState().user);
    if (!sdk) {
      const error = new Error(
        'SDK initialization failed - invalid or missing token'
      );
      dispatch(errorOverwritingScan(existingScan, error));
      if (handleFail) handleFail(error);
      return false;
    }

    if (!existingScan.id) {
      const error = new Error('Scan ID is required for update operation');
      dispatch(errorOverwritingScan(existingScan, error));
      if (handleFail) handleFail(error);
      return false;
    }

    return sdk.scans
      .replaceById(existingScan.id, existingScan)
      .then(() => {
        // SDK returns void on success
        // Reducer doesn't use this data, so pass null
        if (handleSuccess) {
          handleSuccess();
        }

        return dispatch(overwriteScanResponse(null));
      })
      .catch((ex) => {
        redirectToLoginIfRequired(ex);
        dispatch(errorOverwritingScan(existingScan, ex));
        if (handleFail) handleFail(ex);
      });
  };
}

/**
 * Deletes a user scan using the insight-user-sdk
 * @param {string} scanId - The ID of the scan to delete
 * @param {Function} handleSuccess - Success callback
 * @param {Function} handleFail - Failure callback
 * @returns {Function} Redux thunk function
 * @example
 * dispatch(deleteScan('scanId', successCallback, failCallback))
 */
export function deleteScan(scanId, handleSuccess, handleFail) {
  return (dispatch, getState) => {
    const sdk = initializeUserSdk(getState().user);
    if (!sdk) {
      const error = new Error(
        'SDK initialization failed - invalid or missing token'
      );
      dispatch(errorDeletingScan(scanId, error));
      if (handleFail) handleFail(error);
      return false;
    }

    if (!scanId) {
      const error = new Error('Scan ID is required for delete operation');
      dispatch(errorDeletingScan(scanId, error));
      if (handleFail) handleFail(error);
      return false;
    }

    return sdk.scans
      .deleteById(scanId)
      .then(() => {
        // SDK returns void on success
        // Reducer only uses scanId, so pass null for data
        if (handleSuccess) {
          handleSuccess();
        }

        return dispatch(deleteScanResponse(null, scanId));
      })
      .catch((ex) => {
        redirectToLoginIfRequired(ex);
        dispatch(errorDeletingScan(scanId, ex));
        if (handleFail) handleFail(ex);
      });
  };
}

/**
 * Renames an existing user scan by updating the name field.
 * Performs an optimistic update in the Redux store, rolling back on failure.
 * @param {Object} scan - The existing scan object (must include id, name, fields)
 * @param {string} newName - The new name for the scan
 * @returns {Function} Redux thunk function
 * @example
 * dispatch(renameScan(scan, 'New Scan Name'))
 */
export function renameScan(scan, newName) {
  return (dispatch, getState) => {
    if (!scan.id) {
      const error = new Error('Scan ID is required for rename operation');
      dispatch(errorRenamingScan(scan, error));
      return false;
    }

    const renamedScan = { ...scan, name: newName };

    // Optimistic update
    dispatch({ type: types.RENAME_SCAN, data: renamedScan });

    const sdk = initializeUserSdk(getState().user);
    if (!sdk) {
      const error = new Error(
        'SDK initialization failed - invalid or missing token'
      );
      // Rollback
      dispatch({ type: types.RENAME_SCAN, data: scan });
      dispatch(errorRenamingScan(scan, error));
      return false;
    }

    return sdk.scans
      .replaceById(renamedScan.id, renamedScan)
      .catch((ex) => {
        redirectToLoginIfRequired(ex);
        // Rollback
        dispatch({ type: types.RENAME_SCAN, data: scan });
        dispatch(errorRenamingScan(scan, ex));
      });
  };
}

/**
 * Counts conversations opened in this app instance, so the fallback id below is
 * unique even for two sessions started within the same millisecond.
 */
let aiScannerSessionCount = 0;

/**
 * STORY-011 (#719): starts an AI Scanner conversation.
 *
 * `sessionId` is a correlation id only: Syphon echoes it back in
 * `data.sessionId` and never uses it as a key, so it must be unique, not
 * unguessable. The fallback is a counter rather than a pseudo-random number —
 * a PRNG raises the question of whether the value is security-relevant
 * (SonarQube S2245) when it is not, and a counter cannot collide inside a
 * single millisecond the way a PRNG can.
 *
 * Cleared by {@link resetAiScannerAnswers} when the overlay closes.
 */
export function startAiScannerSession() {
  const webCrypto = typeof crypto !== 'undefined' ? crypto : null;
  aiScannerSessionCount += 1;

  const sessionId =
    webCrypto && typeof webCrypto.randomUUID === 'function'
      ? webCrypto.randomUUID()
      : `ai-${Date.now()}-${aiScannerSessionCount}`;

  return {
    type: types.START_AI_SCANNER_SESSION,
    sessionId: sessionId
  };
}

/** Counts persisted `ChatSession`s so the fallback id below stays unique. */
let chatSessionCount = 0;

/** Mint the top-level `ChatSession.sessionId`, mirroring `startAiScannerSession`. */
function newChatSessionId() {
  const webCrypto = typeof crypto !== 'undefined' ? crypto : null;
  chatSessionCount += 1;
  return webCrypto && typeof webCrypto.randomUUID === 'function'
    ? webCrypto.randomUUID()
    : `chat-${Date.now()}-${chatSessionCount}`;
}

/**
 * STORY (#724): persist one AI Scanner round. The single place that builds the
 * next `ChatSession`: it loads the existing record (minting the top-level
 * `sessionId` once, then reusing it — FR-003a) and, keyed on
 * `conversationId`, updates that round in place (same conversation, e.g. a
 * changed answer, or a summary landing for an earlier flow) or appends
 * (re-search).
 *
 * STORY-028 (#739) gives it two callers, and that one rule serves both:
 *  1. `AiScannerContainer.handleAnswersComplete` writes the round the instant
 *     the member reaches the results, with `aiSummary` still null (FR-001).
 *     Its `conversationId` is new, so this APPENDS.
 *  2. {@link requestScanSummary} writes it again when the summary is accepted
 *     as current. Same `conversationId`, so this UPDATES IN PLACE, filling in
 *     the `aiSummary` the first write left null.
 *
 * So a flow is saved even when the summary never lands, and a flow whose
 * summary does land is stored once, not twice. Re-opening hashtags mints a new
 * `conversationId` (via `RESET_AI_SCANNER_ANSWERS`), which is what makes the
 * next flow append rather than overwrite the one before it.
 *
 * Updating in place keeps the stored `checkedSymbols` unless the incoming
 * round carries its own. Every caller builds the round before the member has
 * ticked anything, so without this each re-save — a summary landing, a
 * changed answer — would wipe their ticks. Descriptions and industries filled
 * in by {@link fillAiScannerRoundMetadata} are kept for the same reason.
 *
 * @param {object} round - A `SearchRound` including `conversationId`.
 * @returns {Function} Redux thunk.
 */
export function persistAiScannerRound(round) {
  return () => {
    const existing = aiScannerSession.load();
    const base = existing || {
      schemaVersion: aiScannerSession.SCHEMA_VERSION,
      sessionId: newChatSessionId(),
      searchRounds: []
    };

    const rounds = base.searchRounds.slice();
    // STORY-028 (#739): the match is looked for ANYWHERE in the list, not just
    // at the tail. A summary can land after the member has re-opened hashtags
    // and completed the next flow, so the round it belongs to is no longer the
    // last one — comparing against the tail alone would append a second copy
    // of a round that is already stored.
    const index = rounds.findIndex(
      (stored) => stored.conversationId === round.conversationId
    );
    if (index !== -1) {
      // Same conversation — update in place.
      const { checkedSymbols, results: storedResults } = rounds[index];
      const merged = {
        ...round,
        results: fillMissingResultMetadata(round.results, storedResults)
      };
      rounds[index] =
        checkedSymbols && !round.checkedSymbols
          ? { ...merged, checkedSymbols }
          : merged;
    } else {
      rounds.push(round); // first round or re-search — append
    }

    aiScannerSession.save({ ...base, searchRounds: rounds });
  };
}

/**
 * STORY-015 (#729): drop a stored round again.
 *
 * `persistAiScannerRound` writes a round the moment its results are reached
 * (STORY-028 #739 FR-001), so that a member reading their cards has something
 * saved even if the summary never lands. That eagerness needs an undo: a round
 * the member then backs out of — answering 「はい」 to the re-search prompt and
 * changing their mind to 「いいえ」 — is already in storage, and without this it
 * would replay as history for a search they abandoned.
 *
 * A no-op when nothing carries the id, so callers need not track whether the
 * round was ever written.
 *
 * @param {?string} conversationId - The round to remove.
 * @returns {Function} Redux thunk.
 */
export function removeAiScannerRound(conversationId) {
  return () => {
    if (!conversationId) return;

    // Dropping the stored round is not enough on its own: the summary request
    // fired alongside it may still be in flight, and Bedrock takes seconds.
    // `ownsRound` below only rejects a response that a NEWER request for the
    // same conversation has superseded — and backing out of a round issues no
    // new request, so a late response would still be judged current and
    // `persistAiScannerRound` would append the round straight back, now
    // carrying a summary. Forgetting the conversation makes `ownsRound` false
    // for anything still outstanding, so the response is dropped and never
    // reaches storage or the screen.
    //
    // Done before the storage read below, which can return early: the
    // invalidation is owed as soon as the caller declares the round abandoned,
    // whether or not there is anything left to delete.
    summaryRequestIdByConversation.delete(conversationId);

    const existing = aiScannerSession.load();
    if (!existing) return;

    const rounds = existing.searchRounds.filter(
      (stored) => stored.conversationId !== conversationId
    );
    if (rounds.length === existing.searchRounds.length) return;

    aiScannerSession.save({ ...existing, searchRounds: rounds });
  };
}

/**
 * Save the symbols ticked in one stored round, replacing what it held.
 *
 * A no-op when nothing is saved or no round carries the id — the ticks still
 * work on screen, there is just nothing to attach them to.
 *
 * @param {?string} conversationId - The round the ticks belong to.
 * @param {string[]} checkedSymbols - Every symbol now ticked in that round.
 * @returns {Function} Redux thunk.
 */
export function persistAiScannerCheckedSymbols(conversationId, checkedSymbols) {
  return () => {
    if (!conversationId) return;

    const existing = aiScannerSession.load();
    if (!existing) return;

    const index = existing.searchRounds.findIndex(
      (stored) => stored.conversationId === conversationId
    );
    if (index === -1) return;

    const rounds = existing.searchRounds.slice();
    rounds[index] = { ...rounds[index], checkedSymbols };
    aiScannerSession.save({ ...existing, searchRounds: rounds });
  };
}

/**
 * Fill a stored round's empty descriptions and industries from `candidates`.
 * The round is saved the moment Q2 is answered, which can be before the
 * symbol metadata lands; without this its history copy keeps 「ー」.
 *
 * A no-op when nothing is saved, no round carries the id, or nothing is new.
 *
 * @param {?string} conversationId - The round to fill.
 * @param {Array<object>} candidates - Live candidates for that round.
 * @returns {Function} Redux thunk.
 */
export function fillAiScannerRoundMetadata(conversationId, candidates) {
  return () => {
    if (!conversationId) return;

    const existing = aiScannerSession.load();
    if (!existing) return;

    const index = existing.searchRounds.findIndex(
      (stored) => stored.conversationId === conversationId
    );
    if (index === -1) return;

    const stored = existing.searchRounds[index];
    const results = fillMissingResultMetadata(stored.results, candidates);
    if (results === stored.results) return;

    const rounds = existing.searchRounds.slice();
    rounds[index] = { ...stored, results };
    aiScannerSession.save({ ...existing, searchRounds: rounds });
  };
}

/**
 * Monotonic stamp on every summary request.
 *
 * Answering Q2 and then CHANGING Q1 fires a second request while the first may
 * still be in flight, and Bedrock latency varies by seconds — so responses can
 * arrive out of order. The reducer drops any result whose id is not the newest
 * request, making the member's latest answers always win.
 */
let summaryRequestId = 0;

/**
 * STORY-028 (#739): the newest summary request id PER CONVERSATION.
 *
 * The counter above is global, and that is right for the reducer: only one
 * summary is on screen, so the newest request always owns it. Persistence is
 * not the same question. Re-opening hashtags lets a member start a second flow
 * while the first flow's summary is still in flight, and that first summary is
 * not stale — it is the only summary its own (now frozen) round will ever get.
 * Judged by the global counter it would be discarded, leaving the round stored
 * with `aiSummary: null` forever.
 *
 * So the persist guard asks the narrower question: is this response the newest
 * request for ITS OWN round? A changed Q1 still re-fires within one
 * conversation and still supersedes the previous request for it. One entry per
 * conversation started in this page's lifetime — a handful per sitting.
 */
const summaryRequestIdByConversation = new Map();

function requestingScanSummary(requestId) {
  return { type: types.REQUESTING_SCAN_SUMMARY, requestId: requestId };
}

function receiveScanSummary(summaryText, requestId) {
  return {
    type: types.RECEIVE_SCAN_SUMMARY,
    summaryText: summaryText,
    requestId: requestId
  };
}

export function errorRequestingScanSummary(errorCode, errorMessage, requestId) {
  return {
    type: types.ERROR_REQUESTING_SCAN_SUMMARY,
    errorCode: errorCode || 'E0045',
    errorMessage: errorMessage,
    requestId: requestId
  };
}

function getSummarizeUrl() {
  // prettier-ignore
  return `https://${process.env.REACT_APP_INSIGHT_API_DOMAIN}/market/scans/summarize`;
}

/**
 * STORY-011 (#719): posts the assembled request to Syphon and stores the
 * generated summary.
 *
 * Deliberately NOT `fetchRetry`: its 3s default timeout is shorter than a
 * normal Bedrock generation (Syphon's own sample reports `runTime: 3170`), so
 * it would time out and re-invoke the model up to three times. The
 * loading/timeout/fallback UX is a separate follow-up story.
 *
 * @param {object} body - From `buildSummarizeRequest` (utils/summarizePayload).
 * @param {object} [round] - STORY (#724): completed `SearchRound` (minus
 *   `aiSummary`) to persist once the summary is accepted as current. Omit to
 *   skip persistence.
 * @returns {Function} Redux thunk.
 */
export function requestScanSummary(body, round) {
  return (dispatch, getState) => {
    summaryRequestId += 1;
    const requestId = summaryRequestId;
    if (round) {
      summaryRequestIdByConversation.set(round.conversationId, requestId);
    }

    // Claimed BEFORE the guards below, not after. The reducer drops any result
    // whose id is not the current one, so an early-return error stamped with a
    // id the store has never seen would be discarded as stale — silently losing
    // exactly the errors AC C19978733 asks us to surface.
    dispatch(requestingScanSummary(requestId));

    const user = getState().user;
    if (!validateUser(user)) {
      dispatch(
        errorRequestingScanSummary(
          'E0022',
          'Invalid or missing user token',
          requestId
        )
      );
      return false;
    }
    if (!body) {
      dispatch(
        errorRequestingScanSummary(
          'INVALID_REQUEST',
          'Summary request could not be assembled from the current state',
          requestId
        )
      );
      return false;
    }

    return fetch(getSummarizeUrl(), {
      method: 'post',
      headers: {
        Accept: 'application/json, text/plain, */*',
        'Content-Type': 'application/json',
        authorization: user.token
      },
      body: JSON.stringify(body)
    })
      // The HTTP status is carried alongside the parsed body: a failure that
      // never reaches Syphon (gateway 404/502, expired-token 401) has no error
      // envelope, so the status is the only description of the cause available.
      // `json()` itself is guarded because such a response is often HTML.
      .then((response) =>
        response
          .json()
          .catch(() => null)
          .then((payload) => ({
            payload: payload,
            status: response.status,
            statusText: response.statusText
          }))
      )
      .then(({ payload, status, statusText }) => {
        // The envelope carries `invokeid`/`version`/`recvTime`/`runTime`
        // alongside the payload; only `ok` and `data.summaryText` are read.
        if (!payload || payload.ok !== true) {
          const errorCode = (payload && payload.errorCode) || 'E0045';
          // Never left undefined: AC C19978733 requires the message to describe
          // the cause, and a non-envelope response carries no `errorMessage`.
          // `${status} ${statusText}`.trim() rather than a conditional: an empty
          // statusText just collapses, so there is no branch to get wrong.
          const errorMessage =
            (payload && payload.errorMessage) ||
            `The summary service responded with ${`${status} ${statusText}`.trim()}.`;
          Logger.error({
            errorCode: errorCode,
            message: `Scan summary rejected: ${errorMessage}`
          });
          return dispatch(
            errorRequestingScanSummary(errorCode, errorMessage, requestId)
          );
        }

        const summaryText = payload.data && payload.data.summaryText;
        // Trimmed once and stored trimmed: Bedrock output routinely carries a
        // trailing newline, and the results display may well render with
        // `pre-wrap` to keep paragraph breaks — in which case leading or
        // trailing blank lines would be visible. Internal newlines survive.
        const trimmedSummary =
          typeof summaryText === 'string' ? summaryText.trim() : '';

        if (trimmedSummary.length === 0) {
          return dispatch(
            errorRequestingScanSummary(
              'EMPTY_SUMMARY',
              'The response contained no summary text',
              requestId
            )
          );
        }

        const accepted = dispatch(receiveScanSummary(trimmedSummary, requestId));

        // STORY (#724): persist the round only if this is still the current
        // request for that round, so an out-of-order response can't overwrite
        // a newer answer set. STORY-028 (#739) scopes that to the round's own
        // conversation — see `summaryRequestIdByConversation`.
        const ownsRound =
          summaryRequestIdByConversation.get(round && round.conversationId) ===
          requestId;

        if (round && ownsRound) {
          dispatch(
            persistAiScannerRound({ ...round, aiSummary: trimmedSummary })
          );
        }

        // STORY-028 (#739): the caller renders a frozen round from this
        // return value, so it has to answer the SAME question the persist
        // guard just asked — otherwise the screen and localStorage are fed by
        // two different decisions about one round and can disagree. A summary
        // superseded within its own conversation (the member changed an
        // answer) is not this round's summary: the store rejected it and
        // nothing was persisted, so it must not reach the screen either.
        if (round && !ownsRound) return false;

        return accepted;
      })
      .catch((ex) => {
        redirectToLoginIfRequired(ex);
        // Same rule as the response path: never leave the message empty, or the
        // error carries no cause (AC C19978733). A rejection is not guaranteed
        // to be an Error — an offline fetch can reject with anything.
        dispatch(
          errorRequestingScanSummary(
            'E0045',
            (ex && ex.message) || 'The summary request could not be completed.',
            requestId
          )
        );
      });
  };
}
